import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  createCampaign,
  errorMessage,
  type CampaignDetail,
  type CampaignSettings,
  type EmailProjectAccess,
  type EmailSender,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScopeBadge } from "@/components/email/EmailBadges";
import { SenderField } from "@/components/email/SenderField";
import {
  DEFAULT_SETTINGS,
  SendingSettingsFields,
  settingsProblem,
} from "@/components/email/SendingSettingsFields";

/** Name and sending settings of a new campaign of the current project. */
export function NewCampaignDialog({
  open,
  onOpenChange,
  slug,
  access,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  /** The current project: the campaign is its own. */
  access: EmailProjectAccess;
  onCreated: (campaign: CampaignDetail) => void;
}) {
  const [name, setName] = useState("");
  const [settings, setSettings] = useState<CampaignSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setSettings(DEFAULT_SETTINGS);
    setError(null);
  }, [open]);

  const setSender = useCallback(
    (sender: EmailSender | null) => setSettings((s) => ({ ...s, sender })),
    [],
  );

  const problem = !name.trim() ? "Name the campaign." : settingsProblem(settings);

  async function submit() {
    if (problem) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createCampaign(slug, { name: name.trim(), settings });
      toast.success(`“${created.name}” created`);
      onOpenChange(false);
      onCreated(created);
    } catch (err) {
      setError(errorMessage(err, "The campaign could not be created."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle>New campaign</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <ScopeBadge scope={access.scope} />
            In {access.name}: it only reaches this project&apos;s contacts.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div className="space-y-1.5">
              <Label htmlFor="campaign-name" className="text-xs font-medium text-slate-600">
                Name
              </Label>
              <Input
                id="campaign-name"
                autoFocus
                placeholder="Founders francophones — Q4"
                value={name}
                maxLength={255}
                onChange={(e) => setName(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <p className="type-zonehead">Sending</p>
              <SenderField slug={slug} value={settings.sender} onChange={setSender} />
              <SendingSettingsFields value={settings} onChange={setSettings} />
            </div>
            {error && <p className="text-sm text-rose-600">{error}</p>}
          </div>
          <DialogFooter className="gap-2 border-t border-slate-100 px-6 py-3.5">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!!problem || saving} title={problem ?? undefined}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Create campaign
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
